/**
 * Registration Flow State Machine Types
 *
 * This defines the complete state machine for agent registration.
 * Each state represents a distinct step in the registration process.
 */

/**
 * What the invitee is allowed to learn before they have proven who they are.
 *
 * validateAgentInvitation deliberately returns only enough to address them by
 * name and show their role - not the phone, licence number, or territory on
 * the agent record.
 */
export interface AgentData {
	email: string;
	firstName: string;
	role: 'agent' | 'master_agent' | 'super_agent';
}

// Registration State Machine States
export type RegistrationState =
	| { type: 'IDLE' }
	| { type: 'VALIDATING_INVITATION' }
	| { type: 'INVITATION_INVALID'; error: string }
	| {
			type: 'PASSWORD_SETUP';
			agent: AgentData;
		}
	| {
			type: 'COMPLETE';
			agent: AgentData;
		}
	| {
			type: 'ERROR';
			error: string;
			previousState?: RegistrationState;
		};

// Registration Actions
export type RegistrationAction =
	| { type: 'START_VALIDATION' }
	| { type: 'INVITATION_VALIDATED'; agent: AgentData }
	| { type: 'INVITATION_FAILED'; error: string }
	| { type: 'REGISTRATION_COMPLETE' }
	| { type: 'ERROR_OCCURRED'; error: string }
	| { type: 'RETRY_FROM_ERROR' }
	| { type: 'RESTORE_STATE'; state: RegistrationState };

// State Machine Reducer
export function registrationReducer(
	state: RegistrationState,
	action: RegistrationAction
): RegistrationState {
	switch (action.type) {
		case 'START_VALIDATION':
			return { type: 'VALIDATING_INVITATION' };

		case 'INVITATION_VALIDATED':
			return {
				type: 'PASSWORD_SETUP',
				agent: action.agent
			};

		case 'INVITATION_FAILED':
			return {
				type: 'INVITATION_INVALID',
				error: action.error
			};

		case 'REGISTRATION_COMPLETE':
			if (state.type === 'PASSWORD_SETUP') {
				return {
					type: 'COMPLETE',
					agent: state.agent
				};
			}
			return state;

		case 'ERROR_OCCURRED':
			return {
				type: 'ERROR',
				error: action.error,
				previousState: state
			};

		case 'RETRY_FROM_ERROR':
			if (state.type === 'ERROR' && state.previousState) {
				return state.previousState;
			}
			return state;

		case 'RESTORE_STATE':
			return action.state;

		default:
			return state;
	}
}

/*
 * There is deliberately no sessionStorage persistence here any more.
 *
 * The old flow serialized the whole state machine, and for PASSWORD_SETUP that
 * object carried the invitation's temporary password in cleartext - restored
 * without any auth check, so any XSS on the origin or a shared browser handed
 * over a working credential. The invitation token now lives only in the URL,
 * which means a reload simply re-validates it and nothing sensitive is ever
 * written to disk.
 */
